// Helpers for dataset item metadata and the dataset-level JSON Schema.
//
// The backend stores `DataSetItem.metadata` as arbitrary JSON and validates it against
// `DataSet.metadata_schema`; these helpers keep the UI's text ⇄ value handling and the server's
// validation-error shape in one place.

/** The JSON text to show in an editor for a stored metadata value. */
export const metadataText = (value) => {
  if (value === null || value === undefined || value === "") return "";
  if (typeof value === "string") return value;

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

/**
 * Parse the editor's text into the value the API takes.
 *
 * Empty text means "no metadata" (`null`), not an error.
 *
 * @returns {{ok: boolean, value: *, message?: string}}
 */
export const parseMetadataText = (text) => {
  const raw = typeof text === "string" ? text.trim() : "";

  if (raw === "") return { ok: true, value: null };

  try {
    return { ok: true, value: JSON.parse(raw) };
  } catch (error) {
    return { ok: false, value: null, message: error?.message || "Invalid JSON." };
  }
};

/** Normalise a server validation response into `[{path, message}]`. */
export const normalizeMetadataErrors = (result) => {
  const list = Array.isArray(result?.errors) ? result.errors : [];

  return list.map((entry) => {
    if (typeof entry === "string") return { path: "$", message: entry };

    return {
      path: entry?.path ?? entry?.instance_path ?? entry?.data_path ?? "$",
      message: entry?.message ?? String(entry),
    };
  });
};

const parseSchema = (schema) => {
  if (typeof schema === "string") {
    try {
      return JSON.parse(schema);
    } catch (error) {
      return null;
    }
  }

  return schema;
};

const applySchemaToProps = (value, schema) => {
  const properties = schema?.properties;

  // No declared properties: nothing to reconcile against (an `additionalProperties: false`
  // schema with no properties means "no fields allowed").
  if (!properties || typeof properties !== "object" || Object.keys(properties).length === 0) {
    return schema?.additionalProperties === false ? {} : value;
  }

  const next = {};

  Object.keys(value).forEach((key) => {
    if (!(key in properties)) return; // removed from the schema → drop the metadata field
    next[key] = applySchema(value[key], properties[key]);
  });

  // Fields the schema declares with a default but the metadata lacks.
  Object.entries(properties).forEach(([key, propertySchema]) => {
    if (key in next) return;
    if (propertySchema && Object.prototype.hasOwnProperty.call(propertySchema, "default")) {
      next[key] = propertySchema.default;
    }
  });

  return next;
};

/** Recurse into a value using its schema: objects by properties, arrays by items. */
const applySchema = (value, schema) => {
  if (!schema || typeof schema !== "object") return value;
  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value.map((item) => applySchema(item, schema.items ?? {}));
  }

  if (typeof value !== "object") return value;

  return applySchemaToProps(value, schema);
};

/**
 * Reconcile a dataset's metadata with its item metadata schema.
 *
 * This is "applying the schema" to the dataset record: object keys the schema no longer declares
 * are dropped (so removing a field from the schema removes it from the metadata), nested objects
 * and array items are reconciled the same way, and fields the schema declares with a `default`
 * are filled in when missing.
 *
 * If the metadata isn't valid JSON, or there is no usable schema, it is returned untouched.
 *
 * @param {object|string|null} metadata
 * @param {object|string|null} schema
 * @returns {*} The reconciled metadata value.
 */
export const reconcileMetadataWithSchema = (metadata, schema) => {
  const parsedSchema = parseSchema(schema);

  if (!parsedSchema || typeof parsedSchema !== "object") return metadata;

  let value = metadata;

  if (typeof value === "string") {
    const text = value.trim();

    if (text === "") value = {};
    else {
      try {
        value = JSON.parse(text);
      } catch (error) {
        return metadata;
      }
    }
  }

  if (value === null || value === undefined) return metadata;

  return applySchema(value, parsedSchema);
};

/** Whether a dataset declares an item metadata schema. */
export const hasMetadataSchema = (dataSet) => {
  const schema = dataSet?.metadata_schema ?? dataSet?.metadataSchema ?? null;

  if (!schema) return false;
  if (typeof schema === "string") return schema.trim() !== "";

  return typeof schema === "object" && Object.keys(schema).length > 0;
};

/** A one-line description of a schema, for the editor and the ingest dialog. */
export const describeSchema = (schema) => {
  if (!schema) return "No schema";
  if (typeof schema === "string") {
    try {
      return describeSchema(JSON.parse(schema));
    } catch (error) {
      return "Schema set";
    }
  }

  if (typeof schema !== "object") return "Schema set";

  const required = Array.isArray(schema.required) ? schema.required : [];
  const properties = schema.properties && typeof schema.properties === "object" ? Object.keys(schema.properties) : [];

  const parts = [];
  if (properties.length > 0) parts.push(`${properties.length} field${properties.length === 1 ? "" : "s"}`);
  if (required.length > 0) parts.push(`required: ${required.join(", ")}`);

  return parts.join(" · ") || "Schema set";
};

export default metadataText;
